import { useEffect, useState } from 'preact/hooks';
import { AdminShell } from './AdminShell';
import { api } from '../../lib/api';
import { Button } from '../../components/Button';
import {
  Table, Th, Td, Pager, Toolbar, Badge, fmtDate, fieldStyle, type Page, PAGE_SIZE,
} from './ui';
import { useAiConfig, type AiConfig, type AiConfigPatch } from './aiConfig';

type AICall = {
  id: string;
  appUserId?: string;
  model: string;
  serviceTier?: string; // OpenAI tier that served the call; '' for others
  flow: string;
  hadImage: boolean;
  imageBytes: number;
  promptTokens: number;
  outputTokens: number;
  thoughtsTokens: number;
  cachedTokens: number;
  totalTokens: number;
  costUsd: number;
  latencyMs: number;
  status: string;
  finishReason?: string;
  httpStatus: number;
  error?: string;
  createdAt: string;
};

type CallsResponse = Page & { calls: AICall[] };

export function AdminAIUsage() {
  return (
    <AdminShell tab="ai">
      {() => (
        <>
          <AiConfigCard />
          <AICallsTable />
        </>
      )}
    </AdminShell>
  );
}

// Admin controls for the estimate chain: the order the providers are tried in,
// plus each configurable provider's API key (stored encrypted server-side —
// only a hint ever comes back), model and enable/disable switch. Modeled on
// the Subscriptions GiftCard.
function AiConfigCard() {
  const { config, notConfigured, loading, busy, msg, save } = useAiConfig();
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('claude-haiku-5-5');
  const [effort, setEffort] = useState('high');
  const [enabled, setEnabled] = useState(true);

  // Sync the form with the loaded config once (and after saves).
  useEffect(() => {
    if (config) {
      setModel(config.model);
      setEffort(config.effort);
      setEnabled(config.fallbackEnabled);
    }
  }, [config]);

  const onSave = async () => {
    const patch: AiConfigPatch = { fallbackEnabled: enabled, effort };
    // With an env key the model comes from ANTHROPIC_MODEL, so storing one here
    // would be dead config that silently disagrees with what actually runs.
    if (!config?.envKey) patch.model = model.trim();
    const key = apiKey.trim();
    if (key) patch.apiKey = key; // absent = leave the stored key alone
    const ok = await save(patch, key ? 'Saved — new API key stored (encrypted).' : 'Saved.');
    if (ok) setApiKey('');
  };

  const onClearKey = () => save({ apiKey: '' }, 'API key cleared.');

  return (
    <div
      style={{
        background: 'var(--card)', borderRadius: 16, boxShadow: '0 0 0 1px var(--line)',
        padding: 18, marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 12 }}>
        AI config — provider chain
      </div>

      {config && (
        <>
          <ProviderOrderSection config={config} busy={busy} save={save} />
          <OpenAiSection config={config} busy={busy} save={save} />
        </>
      )}

      {notConfigured ? (
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>
          The Claude fallback has no key. Set{' '}
          <code style={{ background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 6 }}>
            ANTHROPIC_API_KEY
          </code>{' '}
          in the backend environment, or{' '}
          <code style={{ background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 6 }}>
            SETTINGS_ENC_KEY
          </code>{' '}
          (base64 of 32 random bytes, e.g. <code>openssl rand -base64 32</code>) to manage the key
          from this page instead.
        </div>
      ) : loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
            <strong style={{ color: 'var(--ink-2)' }}>
              {config ? `${chainPos(config, 'claude')}. Claude` : 'Claude'}
            </strong>{' '}
            {config?.envKey
              ? <>Key from server environment: <code>{config.keyHint}</code> — <code>ANTHROPIC_API_KEY</code> overrides any key stored here.</>
              : config?.hasKey
                ? <>Stored key: <code>{config.keyHint ?? '(unreadable — re-save the key)'}</code></>
                : 'No API key stored.'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <input
              type="password"
              value={apiKey}
              onInput={(e) => setApiKey((e.target as HTMLInputElement).value)}
              placeholder={
                config?.envKey
                  ? 'set by ANTHROPIC_API_KEY'
                  : !config?.keyEditable
                    ? 'needs SETTINGS_ENC_KEY'
                    : config?.hasKey
                      ? 'new API key (leave empty to keep current)'
                      : 'sk-ant-…'
              }
              autocomplete="off"
              style={{ ...fieldStyle, flex: '1 1 260px' }}
              disabled={busy || !!config?.envKey || !config?.keyEditable}
            />
            <input
              type="text"
              value={model}
              onInput={(e) => setModel((e.target as HTMLInputElement).value)}
              placeholder="claude-haiku-5-5"
              style={{ ...fieldStyle, flex: '0 1 200px' }}
              disabled={busy || !!config?.envKey}
            />
            <select
              value={effort}
              onChange={(e) => setEffort((e.target as HTMLSelectElement).value)}
              style={{ ...fieldStyle, flex: '0 0 auto' }}
              disabled={busy}
              title="output_config.effort — also used by Vertex Claude"
            >
              {(config?.efforts ?? []).map((v) => <option key={v} value={v}>effort: {v}</option>)}
            </select>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                color: 'var(--ink-2)', cursor: 'pointer', userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled((e.target as HTMLInputElement).checked)}
                disabled={busy}
              />
              Enabled
            </label>
            <Button variant="ember" size="sm" disabled={busy} onClick={onSave}>
              Save
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || !config?.hasKey || !config?.keyEditable}
              onClick={onClearKey}
            >
              Clear key
            </Button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
            {config && <ChainNote config={config} id="claude" />} When the chain reaches it and
            this is enabled, the request is estimated on Claude with the key above. Local-only app
            use is unaffected. The effort applies to Vertex Claude too; higher effort is slower,
            and each Claude attempt only gets 12–15s.
            {config?.envKey && (
              <> The key and model come from the server environment; only the effort and enable
              switch are editable here.</>
            )}
          </div>
        </>
      )}

      {msg && (
        <div style={{ fontSize: 13, marginTop: 10, color: msg.ok ? '#1c7a45' : 'var(--wine)' }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

// The chain's shape, read off the config instead of hardcoded: with the order
// admin-configurable, "primary" and "fallback" would start lying the moment it
// changes, so every label carries the provider's real position instead.
function chainPos(config: AiConfig, id: string): number {
  return config.providerOrder.indexOf(id) + 1;
}

function providerName(config: AiConfig, id: string): string {
  return config.providers.find((p) => p.id === id)?.name ?? id;
}

// Why the server would skip a provider, when the view already knows. Vertex is
// configured from the server environment, so there's nothing to report for it.
function providerStatus(config: AiConfig, id: string): string | null {
  if (id === 'openai') {
    if (!config.openai.enabled) return 'disabled';
    if (config.openai.keyUnreadable) return 'key unreadable';
    if (!config.openai.hasKey && !config.openai.envKey) return 'no key';
  }
  if (id === 'claude') {
    if (!config.fallbackEnabled) return 'disabled';
    if (!config.hasKey && !config.envKey) return 'no key';
  }
  return null;
}

// One line under a provider's controls: where it sits and who follows it.
function ChainNote({ config, id }: { config: AiConfig; id: string }) {
  const after = config.providerOrder
    .slice(config.providerOrder.indexOf(id) + 1)
    .map((p) => providerName(config, p));
  return (
    <>
      Position {chainPos(config, id)} of {config.providerOrder.length} in the chain —{' '}
      {after.length > 0
        ? <>next in line if it fails: {after.join(', ')}.</>
        : <>the last link, so a failure here ends the estimate.</>}
    </>
  );
}

// Provider order — the chain every estimate walks, top to bottom. Up/down only:
// the server wants a full permutation, so there is nothing to add or remove.
function ProviderOrderSection({
  config, busy, save,
}: {
  config: AiConfig;
  busy: boolean;
  save: (patch: AiConfigPatch, okText: string) => Promise<boolean>;
}) {
  const [order, setOrder] = useState(config.providerOrder);

  useEffect(() => { setOrder(config.providerOrder); }, [config]);

  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= order.length) return;
    const next = order.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };
  const dirty = order.join(',') !== config.providerOrder.join(',');

  return (
    <div style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--line-soft)' }}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10, lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--ink-2)' }}>Provider order</strong>{' '}
        Every estimate walks this chain from the top and stops at the first provider that
        answers. Providers that are disabled or have no key are skipped wherever they sit.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
        {order.map((id, i) => {
          const status = providerStatus(config, id);
          return (
            <div
              key={id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink)' }}
            >
              <span style={{ color: 'var(--ink-3)', width: 16 }}>{i + 1}.</span>
              <span style={{ fontWeight: 600 }}>{providerName(config, id)}</span>
              {status && <Badge tone="wine">{status}</Badge>}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || i === 0}
                  title="Move up"
                  onClick={() => move(i, -1)}
                >
                  ↑
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || i === order.length - 1}
                  title="Move down"
                  onClick={() => move(i, 1)}
                >
                  ↓
                </Button>
              </span>
            </div>
          );
        })}
      </div>
      <Button
        variant="ember"
        size="sm"
        disabled={busy || !dirty}
        onClick={() => save({ providerOrder: order }, 'Saved — new provider order.')}
      >
        Save order
      </Button>
    </div>
  );
}

// The OpenAI link of the chain. Key, model, reasoning effort, fast mode and
// on/off are stored in app_settings (the key encrypted) unless OPENAI_API_KEY
// overrides them; where it runs comes from the provider order above.
function OpenAiSection({
  config, busy, save,
}: {
  config: AiConfig;
  busy: boolean;
  save: (patch: AiConfigPatch, okText: string) => Promise<boolean>;
}) {
  const openai = config.openai;
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(openai.model);
  const [effort, setEffort] = useState(openai.effort);
  const [fast, setFast] = useState(openai.fast);
  const [enabled, setEnabled] = useState(openai.enabled);

  useEffect(() => {
    setModel(openai.model);
    setEffort(openai.effort);
    setFast(openai.fast);
    setEnabled(openai.enabled);
  }, [openai]);

  const onSave = async () => {
    const patch: AiConfigPatch = { openaiEnabled: enabled };
    // With the env override the model/effort/fast come from the environment;
    // storing them here would silently disagree with what runs.
    if (!openai.envKey) {
      patch.openaiModel = model.trim();
      patch.openaiEffort = effort;
      patch.openaiFast = fast;
    }
    const key = apiKey.trim();
    if (key) patch.openaiApiKey = key;
    const ok = await save(patch, key ? 'Saved — OpenAI key stored (encrypted).' : 'Saved.');
    if (ok) setApiKey('');
  };

  return (
    <div style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--line-soft)' }}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10, lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--ink-2)' }}>
          {chainPos(config, 'openai')}. {providerName(config, 'openai')}
        </strong>{' '}
        {openai.envKey
          ? <>Key from server environment: <code>{openai.keyHint}</code> — <code>OPENAI_API_KEY</code> overrides anything stored here.</>
          : openai.keyUnreadable
            ? <>A key is stored but can no longer be decrypted (the server secret changed) — paste it again.</>
            : openai.hasKey
              ? <>Stored key: <code>{openai.keyHint}</code></>
              : <>No API key stored — this link is skipped.</>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <input
          type="password"
          value={apiKey}
          onInput={(e) => setApiKey((e.target as HTMLInputElement).value)}
          placeholder={
            openai.envKey
              ? 'set by OPENAI_API_KEY'
              : openai.hasKey
                ? 'new API key (leave empty to keep current)'
                : 'sk-proj-…'
          }
          autocomplete="off"
          style={{ ...fieldStyle, flex: '1 1 260px' }}
          disabled={busy || openai.envKey}
        />
        <input
          type="text"
          value={model}
          onInput={(e) => setModel((e.target as HTMLInputElement).value)}
          placeholder="gpt-6-luna"
          style={{ ...fieldStyle, flex: '0 1 160px' }}
          disabled={busy || openai.envKey}
        />
        <select
          value={effort}
          onChange={(e) => setEffort((e.target as HTMLSelectElement).value)}
          style={{ ...fieldStyle, flex: '0 0 auto' }}
          disabled={busy || openai.envKey}
          title="reasoning.effort"
        >
          {openai.efforts.map((v) => <option key={v} value={v}>effort: {v}</option>)}
        </select>
        <label
          style={{
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
            color: 'var(--ink-2)', cursor: 'pointer', userSelect: 'none',
          }}
          title="service_tier=priority"
        >
          <input
            type="checkbox"
            checked={fast}
            onChange={(e) => setFast((e.target as HTMLInputElement).checked)}
            disabled={busy || openai.envKey}
          />
          Fast mode (priority processing, ~2× price, lower latency)
        </label>
        <label
          style={{
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
            color: 'var(--ink-2)', cursor: 'pointer', userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled((e.target as HTMLInputElement).checked)}
            disabled={busy}
          />
          Enabled
        </label>
        <Button variant="ember" size="sm" disabled={busy} onClick={onSave}>
          Save
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy || !openai.hasKey || openai.envKey}
          onClick={() => save({ openaiApiKey: '' }, 'OpenAI key cleared.')}
        >
          Clear key
        </Button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        <ChainNote config={config} id="openai" />
        {config.keyDerived && (
          <> Keys are encrypted with a key derived from <code>JWT_SECRET</code> — if you ever
          rotate that secret, paste the keys here again.</>
        )}
      </div>
    </div>
  );
}

function AICallsTable() {
  const [rows, setRows] = useState<AICall[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [flow, setFlow] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (search) params.set('search', search);
    if (flow) params.set('flow', flow);
    if (status) params.set('status', status);
    api<CallsResponse>(`/api/admin/ai-calls?${params}`)
      .then((r) => { if (!cancelled) { setRows(r.calls); setTotal(r.total); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [offset, search, flow, status]);

  return (
    <>
      <Toolbar
        search={search}
        onSearch={(v) => { setOffset(0); setSearch(v); }}
        placeholder="Search by app user / model…"
        filters={[
          {
            value: flow,
            onChange: (v) => { setOffset(0); setFlow(v); },
            options: [
              { value: '', label: 'All flows' },
              { value: 'image', label: 'Image' },
              { value: 'text', label: 'Text' },
              { value: 'refine', label: 'Refine' },
            ],
          },
          {
            value: status,
            onChange: (v) => { setOffset(0); setStatus(v); },
            options: [
              { value: '', label: 'All statuses' },
              { value: 'ok', label: 'OK' },
              { value: 'error', label: 'Error' },
            ],
          },
        ]}
      />

      {error && <p style={{ color: 'var(--wine)' }}>{error}</p>}

      <Table>
        <thead>
          <tr>
            <Th>When</Th>
            <Th>Flow</Th>
            <Th>Model</Th>
            <Th>Tokens (in/out)</Th>
            <Th>Cost</Th>
            <Th>Latency</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <Td muted>{fmtDateTime(r.createdAt)}</Td>
              <Td>
                <Badge tone="neutral">{r.flow}</Badge>
                {r.hadImage && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--ink-3)' }}>
                    📷 {fmtKb(r.imageBytes)}
                  </span>
                )}
              </Td>
              <Td muted>
                <span style={{ fontSize: 12 }}>{r.model}</span>
                {/* Only the non-default tier is worth the ink — it doubles cost. */}
                {r.serviceTier && r.serviceTier !== 'default' && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--ink-3)' }}>
                    {r.serviceTier}
                  </span>
                )}
              </Td>
              <Td>
                {r.promptTokens.toLocaleString()} / {r.outputTokens.toLocaleString()}
                {r.cachedTokens > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--ink-3)' }}>
                    ({r.cachedTokens.toLocaleString()} cached)
                  </span>
                )}
              </Td>
              <Td>{r.costUsd > 0 ? `$${r.costUsd.toFixed(5)}` : '—'}</Td>
              <Td muted>{r.latencyMs.toLocaleString()} ms</Td>
              <Td>
                <Badge tone={r.status === 'ok' ? 'green' : 'wine'}>{r.status}</Badge>
                {r.status === 'error' && r.error && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', maxWidth: 220, marginTop: 2 }}>
                    {r.error}
                  </div>
                )}
              </Td>
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr><Td colSpan={7} muted>No AI calls yet.</Td></tr>
          )}
        </tbody>
      </Table>

      <Pager total={total} offset={offset} count={rows.length} loading={loading} onOffset={setOffset} />
    </>
  );
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return fmtDate(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fmtKb(bytes: number): string {
  if (bytes <= 0) return '';
  return `${Math.round(bytes / 1024)} KB`;
}
